import { message } from 'antd';
import { CompactDualSelectWithRequest } from 'emp-3.0/components/Sankey/components/CompactDualSelect';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import Icon from '@ant-design/icons';
import FilterIcon from '@icons/emp/filter-icon.svg';
import PlusIcon from '@icons/emp/plus_icon.svg';
import { usePostShareSankey } from '@src/emp-3.0/api/queryHooks';
import RangePicker from '@src/emp-3.0/components/Sankey/GlobalFilter/RangePicker';
import { Title, Wrapper } from '@src/emp-3.0/components/Sankey/GlobalFilter/styled';
import { IGlobalFilter } from '@src/emp-3.0/components/Sankey/GlobalFilter/typed';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import { ShareButton, SharingModal, updateShareId } from '@src/emp-3.0/components/common/Sharing';
import { trackSankeyShare } from '@src/sensor';
import { formatInterval } from '../utils';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

export const FilterList = ({
  interval,
  filters,
  onChange,
  dimensionOptions,
  fixedDimensions = [] as string[],
  showOneFilter,
  style,
  globalFilters,
}: {
  interval: string;
  filters: any[];
  onChange: (value: any) => void;
  dimensionOptions: any[];
  fixedDimensions?: string[];
  showOneFilter?: boolean;
  style?: React.CSSProperties;
  globalFilters?: any[];
}) => {
  const initFilters = useMemo(() => {
    return filters.map((item) => {
      return {
        field: item[0].field,
        op: item[0].op,
        code: item[0]?.code,
        value: item
          .map((filter) => filter.value)
          .flat()
          .filter((value) => value !== undefined),
      };
    });
  }, [filters]);
  const [fommatFilters, setFommatFilters] = useState(initFilters);

  const dimensionNameOptions = dimensionOptions.map((item) => {
    item.disabled = fommatFilters.some((filter) => filter.field === item.value);
    return item;
  });

  useEffect(() => {
    setFommatFilters(initFilters);
  }, [initFilters]);
  const fomatFiltersToRequest = (filters) => {
    const res = cloneDeep(filters)
      .map((x) => {
        if (!x.value?.length) {
          x.value = [];
          return [x];
        }

        if (x.value.length == 1) return [{ ...x, code: x.code ? x.code[0] : '' }];
        return x.value.map((v, i) => ({ ...x, value: [v], code: x.code ? x.code[i] : '' }));
      })
      .filter(Boolean);
    return res;
  };

  const handleChange = (value, trigerOnChange: boolean = false) => {
    const newFilters = cloneDeep(value);
    const res = fomatFiltersToRequest(newFilters);

    // Only filter has value will trigger onchange.
    if (trigerOnChange || showOneFilter) {
      onChange?.(res);
    }
  };

  const handleDelete = (_, removeIndex) => {
    const newFilters = cloneDeep(fommatFilters);
    const cloneFilters = cloneDeep(fommatFilters);
    newFilters.splice(removeIndex, 1);
    setFommatFilters(newFilters);

    const removedFilter = cloneFilters[removeIndex];

    // Only delete a has value filter will trigger onchange.
    if (removedFilter && removedFilter.value?.length) {
      onChange?.(fomatFiltersToRequest(newFilters));
    }
  };

  const onAddFilter = () => {
    const newFilters = cloneDeep(fommatFilters);
    newFilters.push({ field: '', op: 'eq', value: [] });
    setFommatFilters(newFilters);
  };
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', ...style }}>
      {fommatFilters?.map((item, index) => {
        const field = item.field;
        const value = item.value;
        const isFixedDimension = fixedDimensions?.includes(field);
        let maxCount = Number.MAX_SAFE_INTEGER;
        if (isFixedDimension) {
          maxCount = 1;
        } else {
          maxCount = showOneFilter ? 10 : Number.MAX_SAFE_INTEGER;
        }
        return (
          <CompactDualSelectWithRequest
            key={`${field}_${index}_${interval}`}
            interval={interval}
            filters={globalFilters}
            showDelete={!isFixedDimension && !showOneFilter}
            onDelete={() => handleDelete(item, index)}
            dimensionSelectProps={{
              showArrow: !isFixedDimension,
              value: field,
              options: dimensionNameOptions,
              disabled: isFixedDimension,
              onChange: (value) => {
                const newFilters = cloneDeep(fommatFilters);
                newFilters[index].field = value;
                newFilters[index].value = [];
                setFommatFilters(newFilters);
                handleChange(newFilters);
              },
              style: { minWidth: 90 },
            }}
            dimensionValueSelectProps={{
              value: value,
              onChange: (value, option) => {
                const newFilters = cloneDeep(fommatFilters);
                newFilters[index].value = value;
                newFilters[index].code = Array.isArray(option) ? option.map((i) => i.code) : [];
                setFommatFilters(newFilters);
                handleChange(newFilters, true);
              },
              maxCount: maxCount,
            }}
            style={{ maxWidth: '100%' }}
          />
        );
      })}
      {!showOneFilter && (
        <Icon
          component={PlusIcon}
          style={{
            color: 'rgba(0, 0, 0, 0.45)',
            width: '32px',
            height: '32px',
            borderRadius: '2px',
            border: '1px solid #D9D9D9',
            display: 'inline-flex',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={onAddFilter}
        />
      )}
    </div>
  );
};

const GlobalFilter: React.FC<IGlobalFilter> = ({
  dimensionOptions = [],
  onChange,
  mask,
  calenderBoundry,
}) => {
  const { sankeyData, selectedNodeIds, sankeyRequestBody, checkedOnly, generateSankeyRequestBody } =
    useSankeyStore((state) => state);
  const { filters, interval } = sankeyRequestBody;

  const [visible, setVisible] = useState<boolean>(false);
  const [shareId, setShareId] = useState<number>(-1);

  const { mutate: generateShareSankey, isLoading: isLoadingGenerateShareSankeyId } =
    usePostShareSankey();

  const hasSankeyData = sankeyData?.nodes?.length && sankeyData?.links?.length;

  const generateShareSankeyId = () => {
    const selectedNodes = sankeyData.nodes.filter((node) => selectedNodeIds.includes(node.id));
    const payload = {
      ...generateSankeyRequestBody,
      selected_node_ids: selectedNodeIds,
      checked_only: checkedOnly,
      selected_nodes: selectedNodes,
    };
    trackSankeyShare({ ...payload });
    generateShareSankey(
      {
        payload,
      },
      {
        onSuccess: (data) => {
          setShareId(data.id);
          updateShareId(data.id);
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      },
    );
  };

  const onClickShare = () => {
    generateShareSankeyId();
    setVisible(true);
  };
  const { userInfo } = useInitialInfoStore();

  const disableStyle: React.CSSProperties = mask ? { opacity: 0.6, pointerEvents: 'none' } : {};
  return (
    <Wrapper>
      <div style={{ display: 'inline-flex' }}>
        <div style={{ display: 'inline-flex', flexShrink: 0, alignItems: 'self-start' }}>
          <Title>Paths</Title>
          <Icon
            component={FilterIcon}
            style={{ fontSize: '16px', margin: '0 12px', height: 37, display: 'inline-flex' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FilterList
            interval={interval}
            dimensionOptions={dimensionOptions}
            fixedDimensions={['app_id']}
            filters={filters}
            onChange={(value) => {
              onChange?.({ filters: value });
            }}
            style={disableStyle}
          />
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <RangePicker
          interval={interval || formatInterval('', calenderBoundry)}
          onChange={(value) => {
            if (value !== interval && value !== '') {
              onChange?.({ interval: value });
            }
          }}
          durationLimit={14}
          calenderBoundry={calenderBoundry}
        />
        <ShareButton disabled={!hasSankeyData || mask} onClick={onClickShare} />
      </div>
      <SharingModal
        id={shareId}
        fetching={isLoadingGenerateShareSankeyId}
        modalProps={{
          open: visible,
          onCancel: () => setVisible(false),
        }}
      />
    </Wrapper>
  );
};

export default GlobalFilter;
